import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from 'antd';
import { useCompaniesQuery } from '../../api/companies';
import { selectors, actionCreators, sliceKeys } from '../../model';
import { useFilteredData, filterTypes } from '../../shared/packages/filter';
import { companyBySearch } from '../../shared/configs/filterOptions';
import ItemUI from '../../shared/ui/Item';
import FetchDataWrapper from '../../../../components/FetchDataWrapper';
import ColListWrapper from '../../shared/ui/ColListWrapper';
import { declOfNum } from '../../../../helpers/dspx-utils';
import { ReactComponent as HouseIcon } from '../../../../resource/ModernHouseIcon.svg';
import NotFound from '../../shared/ui/NotFound';

const { selectSearch, createIsActiveSelector } = selectors[sliceKeys.companies];
const { currentChanged } = actionCreators[sliceKeys.companies];

const MemoizedItem = memo(Item);

const CompaniesList = ({ children }) => {
  const { data, isFetching, refetch } = useCompaniesQuery();

  return (
    <FetchDataWrapper
      data={data}
      isFetching={isFetching}
      refetch={refetch}
      errMsg="Не удалось получить список компаний"
    >
      <Content data={data}>{children}</Content>
    </FetchDataWrapper>
  );
};

export default CompaniesList;

function Content({ data, children }) {
  const search = useSelector(selectSearch);

  const filteredData = useFilteredData({
    data,
    value: search,
    filterType: filterTypes.bySearch,
    options: companyBySearch,
  });

  return (
    <div className="colContentWrap">
      {children}
      {filteredData.length ? (
        <ColListWrapper scrollable>
          {filteredData.map((item) => (
            <MemoizedItem key={item.id} item={item} />
          ))}
        </ColListWrapper>
      ) : (
        <NotFound />
      )}
    </div>
  );
}

function Item({ item }) {
  const selectIsActive = createIsActiveSelector(item.id);

  const isActive = useSelector(selectIsActive);

  const dispatch = useDispatch();

  const handleClick = () =>
    dispatch(
      currentChanged(item, ({ onOk, name }) =>
        Modal.confirm({
          title: `В приемке участвуют дома из компании «${name}»`,
          content: `В приемке не могут участвовать дома из разных компаний. При выборе компании «${item.name}» из приемки удалятся выбранные дома». Продолжить?`,
          onCancel: () => {},
          onOk,
        })
      )
    );

  return (
    <ItemUI
      title={item.name}
      iconComponent={HouseIcon}
      description={createDescription(item)}
      isActive={isActive}
      onClick={!isActive ? handleClick : undefined}
    />
  );
}

function createDescription(company) {
  const { countHouses = 0, countClients = 0 } = company;

  const housesPhrase = createPhrase(countHouses, ['дом', 'дома', 'домов']);
  const clientsPhrase = createPhrase(countClients, [
    'зарегистрированный житель',
    'зарегистрированных жителя',
    'зарегистрированных жителей',
  ]);

  return `${housesPhrase} / ${clientsPhrase}`;
}

function createPhrase(num, words) {
  return `${formatNum(num)} ${declOfNum(num, words)}`;
}

function formatNum(num) {
  return new Intl.NumberFormat('ru-RU').format(num);
}
